import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CalendarIcon, ChevronLeftIcon, ClockIcon, XIcon } from './icons'
import './Select.css'
import './PickerFields.css'

/**
 * Uygulamanın kendi tarih ve saat seçicileri (14 Eki 2026).
 *
 * Tarayıcının <input type="date|time"> kutusu her işletim sisteminde başka
 * görünüyor ve uygulamanın diline uymuyordu. Kutu Select ile aynı; açılan
 * panel alanın altında (yer yoksa üstünde), pencerenin dışına taşabilsin diye
 * body'ye çiziliyor.
 */

interface Pos {
  left: number
  top?: number
  bottom?: number
}

function usePopover(width: number, height: number) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<Pos | null>(null)
  const btn = useRef<HTMLButtonElement>(null)

  const place = () => {
    const r = btn.current?.getBoundingClientRect()
    if (!r) return
    const gap = 6
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    const below = window.innerHeight - r.bottom - gap - 8
    if (below >= height || below >= r.top) setPos({ left, top: r.bottom + gap })
    else setPos({ left, bottom: window.innerHeight - r.top + gap })
  }

  useLayoutEffect(() => {
    if (!open) return
    const re = () => place()
    window.addEventListener('resize', re)
    window.addEventListener('scroll', re, true)
    return () => {
      window.removeEventListener('resize', re)
      window.removeEventListener('scroll', re, true)
    }
  })

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
      btn.current?.focus()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open])

  const toggle = () => {
    if (open) setOpen(false)
    else {
      place()
      setOpen(true)
    }
  }
  const close = () => {
    setOpen(false)
    btn.current?.focus()
  }
  return { open, pos, btn, toggle, close }
}

function Field({
  btn,
  open,
  icon,
  text,
  placeholder,
  onToggle,
  onClear,
  label,
}: {
  btn: React.RefObject<HTMLButtonElement>
  open: boolean
  icon: ReactNode
  text: string
  placeholder: string
  onToggle: () => void
  onClear: () => void
  label: string
}) {
  return (
    <div className="pickf">
      <button
        ref={btn}
        type="button"
        className={`uisel pickf__btn ${open ? 'is-open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${text || 'seçilmedi'}`}
        onClick={(e) => {
          e.preventDefault()
          onToggle()
        }}
      >
        {icon}
        <span className={`uisel__label ${text ? '' : 'pickf__ph'}`}>{text || placeholder}</span>
      </button>
      {text && (
        <button type="button" className="pickf__clear" onClick={onClear} aria-label={`${label} temizle`}>
          <XIcon />
        </button>
      )}
    </div>
  )
}

function Panel({ pos, width, onClose, children, label }: { pos: Pos; width: number; onClose: () => void; children: ReactNode; label: string }) {
  return createPortal(
    <>
      <div className="uisel__scrim" onMouseDown={onClose} />
      <div className="pickp" role="dialog" aria-label={label} style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width }}>
        {children}
      </div>
    </>,
    document.body,
  )
}

/* ---------------------------------------------------------------- Tarih */

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
const WEEKDAYS = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz']
const pad = (n: number) => String(n).padStart(2, '0')
const keyOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`
const todayKey = () => {
  const d = new Date()
  return keyOf(d.getFullYear(), d.getMonth(), d.getDate())
}

/** "2026-10-14" ya da boş. */
export function DateField({
  value,
  onChange,
  label,
  placeholder = 'Seçin',
}: {
  value: string
  onChange: (v: string) => void
  label: string
  placeholder?: string
}) {
  const W = 300
  const { open, pos, btn, toggle, close } = usePopover(W, 360)
  const base = value || todayKey()
  const [view, setView] = useState(() => ({ y: Number(base.slice(0, 4)), m: Number(base.slice(5, 7)) - 1 }))
  useEffect(() => {
    if (open) setView({ y: Number(base.slice(0, 4)), m: Number(base.slice(5, 7)) - 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const text = value
    ? new Date(`${value}T12:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
    : ''
  const lead = (new Date(view.y, view.m, 1).getDay() + 6) % 7
  const days = new Date(view.y, view.m + 1, 0).getDate()
  const today = todayKey()
  const step = (by: number) => {
    const m = view.m + by
    setView(m < 0 ? { y: view.y - 1, m: 11 } : m > 11 ? { y: view.y + 1, m: 0 } : { y: view.y, m })
  }
  const pick = (k: string) => {
    onChange(k)
    close()
  }

  return (
    <>
      <Field
        btn={btn}
        open={open}
        icon={<CalendarIcon />}
        text={text}
        placeholder={placeholder}
        onToggle={toggle}
        onClear={() => onChange('')}
        label={label}
      />
      {open && pos && (
        <Panel pos={pos} width={W} onClose={() => close()} label={label}>
          <div className="pickp__head">
            <button type="button" onClick={() => step(-1)} aria-label="Önceki ay">
              <ChevronLeftIcon />
            </button>
            <b>
              {MONTHS[view.m]} {view.y}
            </b>
            <button type="button" className="pickp__next" onClick={() => step(1)} aria-label="Sonraki ay">
              <ChevronLeftIcon />
            </button>
          </div>
          <div className="pickp__week">
            {WEEKDAYS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="pickp__grid">
            {Array.from({ length: lead }, (_, i) => (
              <span key={`b${i}`} />
            ))}
            {Array.from({ length: days }, (_, i) => {
              const k = keyOf(view.y, view.m, i + 1)
              return (
                <button
                  key={k}
                  type="button"
                  className={`${k === value ? 'is-on' : ''} ${k === today ? 'is-today' : ''}`}
                  aria-pressed={k === value}
                  onClick={() => pick(k)}
                >
                  {i + 1}
                </button>
              )
            })}
          </div>
          <div className="pickp__foot">
            <button type="button" onClick={() => pick('')}>
              Temizle
            </button>
            <button type="button" className="is-main" onClick={() => pick(today)}>
              Bugün
            </button>
          </div>
        </Panel>
      )}
    </>
  )
}

/* ----------------------------------------------------------------- Saat */

const HOURS = Array.from({ length: 24 }, (_, h) => pad(h))
const MINUTES = Array.from({ length: 12 }, (_, i) => pad(i * 5))

/** "14:00" ya da boş. Saat seçilince dakika 00 olur, dakikayla değişir. */
export function TimeField({
  value,
  onChange,
  label,
  placeholder = 'Seçin',
}: {
  value: string
  onChange: (v: string) => void
  label: string
  placeholder?: string
}) {
  const W = 260
  const { open, pos, btn, toggle, close } = usePopover(W, 320)
  const [h, m] = value ? value.split(':') : ['', '']
  const hourList = useRef<HTMLDivElement>(null)

  // Açılınca seçili saat görünsün.
  useEffect(() => {
    if (!open) return
    hourList.current?.querySelector('.is-on')?.scrollIntoView({ block: 'center' })
  }, [open])

  return (
    <>
      <Field
        btn={btn}
        open={open}
        icon={<ClockIcon />}
        text={value ? value.replace(':', '.') : ''}
        placeholder={placeholder}
        onToggle={toggle}
        onClear={() => onChange('')}
        label={label}
      />
      {open && pos && (
        <Panel pos={pos} width={W} onClose={() => close()} label={label}>
          <div className="pickp__time">
            <div className="pickp__col">
              <span className="pickp__cap">Saat</span>
              <div className="pickp__list" ref={hourList}>
                {HOURS.map((x) => (
                  <button
                    key={x}
                    type="button"
                    className={x === h ? 'is-on' : ''}
                    aria-pressed={x === h}
                    onClick={() => onChange(`${x}:${m || '00'}`)}
                  >
                    {x}
                  </button>
                ))}
              </div>
            </div>
            <div className="pickp__col">
              <span className="pickp__cap">Dakika</span>
              <div className="pickp__list">
                {MINUTES.map((x) => (
                  <button
                    key={x}
                    type="button"
                    className={x === m ? 'is-on' : ''}
                    aria-pressed={x === m}
                    onClick={() => {
                      onChange(`${h || '00'}:${x}`)
                      close()
                    }}
                  >
                    {x}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="pickp__foot">
            <button
              type="button"
              onClick={() => {
                onChange('')
                close()
              }}
            >
              Temizle
            </button>
            <button type="button" className="is-main" onClick={() => close()}>
              Tamam
            </button>
          </div>
        </Panel>
      )}
    </>
  )
}
